import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { CampaignOutlined, OpenInNew, Send } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';

import { useApi } from '@shared/services/api';

interface ReleaseNoteRow {
  id: string;
  title: string;
  url: string;
  publishedAt: string;
  announced: boolean;
  announcedAt: string | null;
  inAppSent: number;
  emailSent: number;
  willNotifyInApp: boolean;
  willEmail: boolean;
}

interface ListResponse {
  audienceSize: number;
  releases: ReleaseNoteRow[];
}

interface TestResult {
  releaseId: string;
  title: string;
  email: string;
  inAppSent: boolean;
  emailSent: boolean;
}

interface AnnounceResult {
  releaseId: string;
  title: string;
  dryRun: boolean;
  alreadyAnnounced: boolean;
  audienceSize: number;
  inAppSent: number;
  emailSent: number;
  emailSuppressed: number;
  errors: { userId: number; channel: string; message: string }[];
}

/**
 * Pull a message out of either shape `useApi` can return: the API's own
 * `{ success: false, error: { message } }` envelope, or the client's transport
 * error object.
 */
function errorMessage(res: any, fallback: string): string {
  return res?.error?.message ?? res?.message ?? fallback;
}

/**
 * Announce a published release note.
 *
 * Deliberately a two-step flow: a dry run reports who would be reached, and only
 * then does the send button unlock. Announcing cannot be undone — the release is
 * claimed by slug before anything is sent, so a second attempt sends nothing and
 * there is no way to recall what already went out.
 */
function AdminReleaseNotes() {
  const api = useApi();

  const [rows, setRows] = useState<ReleaseNoteRow[]>([]);
  const [audienceSize, setAudienceSize] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [busyId, setBusyId] = useState<string | null>(null);
  const [preview, setPreview] = useState<AnnounceResult | null>(null);
  const [confirming, setConfirming] = useState<ReleaseNoteRow | null>(null);
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    const res: any = await api.get('/admin/release-notes');
    const payload = res?.data as ListResponse | undefined;
    if (res?.success && payload) {
      setRows(payload.releases);
      setAudienceSize(payload.audienceSize);
    } else {
      setError(errorMessage(res, 'Could not read the release feed.'));
    }
    setLoading(false);
    // `useApi()` builds a fresh object every render, so listing `api` here would
    // give `load` a new identity each time and the effect below would refetch in
    // a loop. It reads the user from a ref internally, so the captured closure
    // stays correct. Same reason AdminAccountTable omits it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const runDryRun = async (row: ReleaseNoteRow): Promise<void> => {
    setBusyId(row.id);
    setPreview(null);
    setTestResult(null);
    setError(null);
    const res: any = await api.post('/admin/release-notes/announce', { releaseId: row.id });
    const payload = res?.data as AnnounceResult | undefined;
    if (res?.success && payload) {
      setPreview(payload);
      setConfirming(row);
    } else {
      setError(errorMessage(res, 'Dry run failed.'));
    }
    setBusyId(null);
  };

  const sendTest = async (): Promise<void> => {
    if (!confirming) return;
    setTesting(true);
    setTestResult(null);
    setError(null);
    const res: any = await api.post('/admin/release-notes/test', {
      releaseId: confirming.id,
    });
    setTesting(false);
    if (res?.success && res.data) {
      setTestResult(res.data as TestResult);
    } else {
      setError(errorMessage(res, 'Test send failed.'));
    }
  };

  const send = async (): Promise<void> => {
    if (!confirming) return;
    setSending(true);
    const res: any = await api.post('/admin/release-notes/announce', {
      dryRun: false,
      releaseId: confirming.id,
    });
    setSending(false);
    setConfirming(null);
    setPreview(null);

    const payload = res?.data as AnnounceResult | undefined;
    if (!res?.success) {
      setError(errorMessage(res, 'Announce failed.'));
      return;
    }
    if (payload?.errors?.length) {
      setError(`Announced with ${payload.errors.length} per-user failure(s). First: ${payload.errors[0].message}`);
    }
    await load();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h6">Release Notes</Typography>
        {audienceSize !== null && <Chip size="small" label={`${audienceSize} active users`} />}
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={() => void load()}>
          Refresh
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {rows.length === 0 && (
        <Alert severity="info">
          No published release notes. A note only appears here once it has
          <code> draft: false</code> and the marketing site has been deployed — this list is read from the live feed,
          not from the repo.
        </Alert>
      )}

      {rows.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Release</TableCell>
              <TableCell>Published</TableCell>
              <TableCell>Channels</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Announce</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">{row.title}</Typography>
                    <Link href={row.url} target="_blank" rel="noopener noreferrer">
                      <OpenInNew sx={{ fontSize: 14 }} />
                    </Link>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {row.id}
                  </Typography>
                </TableCell>
                <TableCell>{dayjs(row.publishedAt).format('MMM D, YYYY')}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    {row.willNotifyInApp && <Chip size="small" label="In-app" />}
                    {row.willEmail && <Chip size="small" color="warning" label="Email" />}
                  </Stack>
                </TableCell>
                <TableCell>
                  {row.announced ? (
                    <Stack>
                      <Chip size="small" color="success" label="Announced" />
                      <Typography variant="caption" color="text.secondary">
                        {dayjs(row.announcedAt).format('MMM D, h:mma')}
                        {' · '}
                        {row.inAppSent} in-app, {row.emailSent} email
                      </Typography>
                    </Stack>
                  ) : (
                    <Chip size="small" variant="outlined" label="Not announced" />
                  )}
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CampaignOutlined />}
                    disabled={row.announced || busyId === row.id}
                    onClick={() => void runDryRun(row)}
                  >
                    {busyId === row.id ? 'Checking…' : 'Announce…'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={confirming !== null} onClose={() => setConfirming(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Announce “{confirming?.title}”?</DialogTitle>
        <DialogContent>
          {preview && (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                This is a dry run. Nothing has been sent yet.
              </Typography>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>Audience</TableCell>
                    <TableCell align="right">{preview.audienceSize} active users</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>In-app notifications</TableCell>
                    <TableCell align="right">{preview.inAppSent}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Emails</TableCell>
                    <TableCell align="right">{preview.emailSent}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Opted out of email</TableCell>
                    <TableCell align="right">{preview.emailSuppressed}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Alert severity="warning" sx={{ mt: 2 }}>
                This can’t be undone. Once sent, the release is marked announced and cannot be sent again — and nothing
                already delivered can be recalled.
              </Alert>

              {testResult && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Test sent to <strong>{testResult.email}</strong>
                  {' — '}
                  {[testResult.inAppSent && 'in-app notification', testResult.emailSent && 'email']
                    .filter(Boolean)
                    .join(' and ') || 'nothing (both channels are off for this note)'}
                  . This didn’t consume the real announcement.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          <Button
            onClick={() => void sendTest()}
            disabled={testing || sending}
            startIcon={<Send sx={{ fontSize: 16 }} />}
          >
            {testing ? 'Sending test…' : 'Send test to me'}
          </Button>
          <Box>
            <Button onClick={() => setConfirming(null)} disabled={sending}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={() => void send()}
              disabled={sending || testing}
              sx={{ ml: 1 }}
            >
              {sending ? 'Sending…' : `Send to ${preview?.inAppSent ?? 0} users`}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminReleaseNotes;
